import React, { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import clsx from 'clsx'
import { useIntl } from 'react-intl'
import useToggle from 'hooks/useToggle'
import { sortBy, groupBy, uniqueBy } from 'lib/array/arrayUtils'
import FilterChip from 'LuxKit/Chips/FilterChip'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import Image from 'components/Common/Image'
import LineExpandArrowsAltIcon from 'LuxKit/Icons/line/LineExpandArrowsAltIcon'
import { mediaQueryUp } from 'components/utils/breakpoint'
import MonthTabButtonsGroup from 'components/OfferPage/TourV2/Departures/MonthTabs/MonthTabButtonsGroup'
import Caption from 'LuxKit/Typography/Caption'
import Group from 'components/utils/Group'
import Divider from 'LuxKit/Divider'
import type { CabinsToDisplay } from './CruiseCabinTile'
import CabinTile from './CruiseCabinTile'

const GridContainer = styled.div`
  display: block;

  ${mediaQueryUp.tablet} {
    grid-column-gap: ${rem(20)};
    grid-template-columns: 7fr 17fr;
    display: grid;
  }
`

const CabinsContainer = styled.div`
  position: sticky;
  top: ${rem(5)};

  &.has-sticky-header {
    top: ${rem(120)};
  }
`

const Deck = styled.div`
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.default.six};
  border-radius: ${props => props.theme.borderRadius.S};
  padding: ${rem(20)};
  position: relative;
  max-height: ${rem(600)};
  overflow: hidden;

  &.expanded {
    max-height: none;
    overflow: visible;

    .expand-map-button {
      display: none;
    }
  }

  ${mediaQueryUp.tablet} {
    max-height: unset;
  }
`

const ExpandMapButton = styled(TextButton)`
  position: absolute;
  right: ${rem(8)};
  bottom: ${rem(8)};
  display: flex;

  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const cabinShowOrder = ['Inside', 'Oceanview', 'Window', 'Balcony', 'Suite']

interface Props {
  ship: App.CruiseShip
  showPackageOptions: () => void
  hasGotToOverview: boolean
}
function CruiseDecksAndCabins(props: Props) {
  const { ship, showPackageOptions, hasGotToOverview } = props
  const intl = useIntl()
  const [selectedDeck, setSelectedDeck] = useState<App.CruiseShipDeck>()
  const [isMapExpanded, toggleExpandMap] = useToggle(false)

  const shipDecks = useMemo(() => {
    const decksByNumber = uniqueBy(ship.decks ?? [], (deck) => deck.deckNumber)
    return sortBy(decksByNumber, ({ deckNumber }) => deckNumber, 'asc')
  }, [ship.decks])

  const cabinFiltersRef = useRef<HTMLDivElement | null>(null)

  const selectDeck = useCallback((deckId: string) => {
    setSelectedDeck(shipDecks.find(deck => deck.id === deckId))
  }, [shipDecks])

  const cabinsToShow = useMemo(() => {
    const categories = ship.cabinCategories ?? []
    const grouped = groupBy(categories, (category) => category.cabinType)
    const groupedItems = [...grouped.entries()]

    const gallery: CabinsToDisplay = {}

    groupedItems.forEach(([key, cabins]) => {
      const images = cabins.flatMap((cabin) => cabin.imageId).filter(Boolean) as Array<string>
      const colors = cabins.flatMap((cabin) => cabin.color).filter(Boolean) as Array<string>

      gallery[key] = {
        images: [...new Set(images)].map((imageId) => ({ id: imageId, type: 'image' })),
        colors: [...new Set(colors)],
        info: cabins[0],
      }
    })

    return gallery
  }, [ship.cabinCategories])

  const availableCabinTypes = useMemo(() => cabinShowOrder.filter((key) => key in cabinsToShow), [cabinsToShow])

  useEffect(() => {
    setSelectedDeck(shipDecks[0]) // default on load
  }, [shipDecks])

  return (
    <Group direction="vertical" gap={24}>
      <MonthTabButtonsGroup ref={cabinFiltersRef} gap={8}>
        {shipDecks.map((deck) => (
          <FilterChip
            onClick={() => selectDeck(deck.id)}
            selected={deck.id === selectedDeck?.id}
            key={deck.id}
            size="large"
          >{deck.name}
          </FilterChip>
        ))}
      </MonthTabButtonsGroup>

      <GridContainer>
        <Deck className={clsx({ expanded: isMapExpanded })} data-testid="deck-image">
          <Group direction="vertical" gap={16}>
            <Heading variant="heading4" colour="neutral-one" align="center">{selectedDeck?.name}</Heading>

            <Divider kind="secondary" />

            <Image
              id={selectedDeck?.imageId}
              quality="best"
              loading="lazy"
              alt={selectedDeck?.name}
              fit="center"
            />

            <ExpandMapButton
              kind="primary"
              startIcon={<LineExpandArrowsAltIcon />}
              className="expand-map-button"
              onClick={toggleExpandMap}
            >
              {intl.formatMessage({
                id: 'J8RhJsT0Ib',
                defaultMessage: 'Expand Map',
                description: 'Button text to expand cruise deck map view',
              })}
            </ExpandMapButton>
          </Group>
        </Deck>

        <CabinsContainer
          className={clsx({ 'has-sticky-header': hasGotToOverview })}
          data-testid="cabin-list"
        >
          <Group direction="vertical" gap={20}>
            <div>
              {availableCabinTypes.map((key) => (
                <CabinTile
                  key={cabinsToShow[key].info.imageId}
                  cabin={cabinsToShow[key]}
                  cabinCategories={ship.cabinCategories}
                  showPackageOptions={showPackageOptions}
                />
              ))}
            </div>
            <Caption variant="medium">
              {intl.formatMessage({
                id: 'qCQOANw5ay',
                defaultMessage: 'Note: cabin photos are provided for illustrative purposes. The actual cabin may differ.',
                description: 'Disclaimer note about cabin photos in cruise decks and cabins section',
              })}
            </Caption>
          </Group>
        </CabinsContainer>
      </GridContainer>
    </Group>
  )
}

export default CruiseDecksAndCabins
